import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { apiErrorMessage } from '../lib/apiError';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter your credentials');
      return;
    }
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user?.role !== 'admin') {
        toast.error('Admin access required');
        setLoading(false);
        return;
      }
      toast.success('Welcome back');
      navigate('/admin', { replace: true });
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Invalid credentials'));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('/images/resort-pond.jpg')` }}
      />
      <div className="absolute inset-0 bg-void/75" />

      {/* Decorative gold line top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/60 to-transparent" />

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-sm mx-4"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 border border-gold-500/40 mb-4 bg-void/60 backdrop-blur-sm">
            <ShieldCheck className="text-gold-500" size={26} />
          </div>
          <p className="overline text-gold-500 tracking-widest text-xs mb-1">Rudragarh Resort</p>
          <h1 className="font-heading text-3xl text-white">Admin Portal</h1>
        </div>

        {/* Form card */}
        <div className="bg-void/80 backdrop-blur-md border border-gold-500/20 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs text-foreground/50 uppercase tracking-widest mb-2 block">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gold-500/70" size={16} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full input-default pl-11 pr-4 text-sm"
                  placeholder="admin@rudragarh.com"
                  autoComplete="email"
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-foreground/50 uppercase tracking-widest mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gold-500/70" size={16} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full input-default pl-11 pr-11 text-sm"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-gold-500 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>

        {/* Footer link */}
        <p className="text-center mt-6">
          <a
            href="/"
            className="text-xs text-foreground/40 hover:text-gold-500 transition-colors tracking-widest uppercase"
          >
            ← Return to Website
          </a>
        </p>
      </motion.div>

      {/* Decorative gold line bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/60 to-transparent" />
    </div>
  );
};

export default AdminLogin;
